/**
 * CSRF Token Management for UI
 *
 * Fetches and manages CSRF tokens for protected API operations
 */

import { logError } from '@/lib/logger';
import { API_BASE_URL } from '@/lib/api/config';

const API_URL = API_BASE_URL;

let csrfToken: string | null = null;
let tokenPromise: Promise<string> | null = null;

/**
 * Fetch CSRF token from backend
 */
async function fetchCSRFToken(): Promise<string> {
  try {
    const response = await fetch(`${API_URL}/api/v1/csrf-token`, {
      method: 'GET',
      credentials: 'include', // Important: include cookies
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSRF token: ${response.status}`);
    }

    const data = (await response.json()) as { token?: string };

    if (!data.token || typeof data.token !== 'string') {
      throw new Error('CSRF token not found in response');
    }

    csrfToken = data.token;
    return csrfToken as string;
  } catch (error) {
    logError(
      'Failed to fetch token',
      { module: 'CSRF', function: 'fetchCSRFToken' },
      error instanceof Error ? error : undefined
    );
    throw error;
  }
}

/**
 * Get current CSRF token (fetch if needed)
 *
 * Uses a promise cache to prevent multiple simultaneous requests
 */
export async function getCSRFToken(): Promise<string> {
  // If we have a token, return it
  if (csrfToken) {
    return csrfToken;
  }

  // If a fetch is in progress, wait for it
  if (tokenPromise) {
    return tokenPromise;
  }

  // Start a new fetch
  tokenPromise = fetchCSRFToken();

  try {
    const token = await tokenPromise;
    tokenPromise = null;
    return token;
  } catch (error) {
    tokenPromise = null;
    throw error;
  }
}

/**
 * Add CSRF token to fetch options
 *
 * Usage:
 * ```typescript
 * const options = await withCSRF({ method: 'POST', body: ... });
 * const response = await fetch('/api/v1/endpoint', options);
 * ```
 */
export async function withCSRF(options: RequestInit = {}): Promise<RequestInit> {
  try {
    const token = await getCSRFToken();

    return {
      ...options,
      headers: {
        ...options.headers,
        'X-CSRF-Token': token,
      },
      credentials: 'include', // Ensure cookies are sent
    };
  } catch (error) {
    logError(
      'Failed to add CSRF token',
      { module: 'CSRF', function: 'withCSRF' },
      error instanceof Error ? error : undefined
    );
    // Return original options if CSRF fetch fails
    // This allows graceful degradation
    return options;
  }
}

/**
 * Did the server reject this request because the STANDARD CSRF token is stale?
 *
 * This is the one failure where repeating the identical request can succeed —
 * but only after `refreshCSRFToken()`, since the token is cached in the module
 * variable above and a repeat would otherwise re-send the same dead value.
 *
 * Shared on purpose. Both transports need this verdict: `fetchWithRetry` in
 * fetch-utils.ts and the XHR upload path in useRecordsUpload.ts. Two separate
 * copies of one rule drift silently — whoever edits one has no reason to look
 * for the other — and they drift precisely on the rare branch the rule exists
 * for. (`evaluateBalanceGate` is the same shape: one predicate, two callers.)
 *
 * The `code` check is load-bearing and must not be relaxed to matching the word
 * "CSRF" in the message. Embedded Bitrix24 routes carry a different,
 * session-bound token, and their 403 ("Session missing CSRF token") must NOT
 * refresh the standard token — that would overwrite a correct embedded header
 * and spend a retry attempt achieving nothing.
 *
 * More generally: the response text is written for a human and may be reworded
 * or translated at any time. Decisions are made on `status` and `code`, never
 * on the wording.
 */
export function isCSRFTokenExpired(status: number, code?: string): boolean {
  return status === 403 && code === 'CSRF_VALIDATION_FAILED';
}

/**
 * Clear cached token (e.g., on logout)
 */
export function clearCSRFToken(): void {
  csrfToken = null;
  tokenPromise = null;
}

/**
 * Refresh token (fetch a new one)
 */
export async function refreshCSRFToken(): Promise<string> {
  csrfToken = null;
  tokenPromise = null;
  return getCSRFToken();
}
